/**
 * Cart Page — Ajax enhancement for the /cart form.
 *
 * Quantity changes, line removal and discount removal go through
 * @theme/cart-mutations; the page morphs from the queue's
 * CartUpdateEvent. Checkout waits for pending work, then resubmits the
 * native form. Without JS the form still posts `updates[...]` natively.
 *
 * Usage (in Liquid):
 *   <cart-page data-cart-section-id="{{ section.id }}">...</cart-page>
 */

import { morph } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { cartMutations } from '@theme/cart-mutations';

const QUANTITY_DEBOUNCE_MS = 500;

/**
 * Gift wrapping that failed to save or is missing its required details
 * stops checkout. gift-wrap.js only loads when configured, so this calls
 * the element's method only if it's defined.
 */
function giftWrapBlocks(root) {
  const el = root.querySelector('gift-wrap');
  return typeof el?.checkoutBlocked === 'function' && el.checkoutBlocked();
}

class CartPage extends HTMLElement {
  #abort = null;
  #timers = new Map();
  /** key → { quantity, writes }: the customer's latest quantity, kept until its writes settle. */
  #intended = new Map();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick.bind(this), { signal });
    this.addEventListener('change', this.#onChange.bind(this), { signal });
    this.addEventListener('submit', this.#onSubmit.bind(this), { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    for (const { timer } of this.#timers.values()) clearTimeout(timer);
    this.#timers.clear();
  }

  #onClick(event) {
    const discountBtn = event.target.closest('[data-action="remove-discount"]');
    if (discountBtn) {
      event.preventDefault();
      const code = discountBtn.closest('[data-discount-code]')?.dataset.discountCode;
      if (code) this.#removeDiscount(code);
      return;
    }

    const row = event.target.closest('[data-key]');
    if (!row) return;
    const key = row.dataset.key;

    if (event.target.closest('.cart__remove-btn')) {
      event.preventDefault();
      this.#cancelTimer(key);
      this.#setLine(key, 0, row);
      return;
    }

    const stepBtn = event.target.closest('.cart__quantity-btn[data-action]');
    const input = row.querySelector('.cart__quantity-input');
    if (!stepBtn || !input) return;

    const current = this.#intended.get(key)?.quantity ?? (parseInt(input.value, 10) || 1);
    input.value = Math.max(0, current + (stepBtn.dataset.action === 'increase' ? 1 : -1));
    this.#scheduleQuantity(key, input, row);
  }

  #onChange(event) {
    const input = event.target.closest('.cart__quantity-input');
    const row = input?.closest('[data-key]');
    if (row) this.#scheduleQuantity(row.dataset.key, input, row);
  }

  /** Checkout waits for pending changes, then resubmits the native form once. */
  #onSubmit(event) {
    const form = event.target;
    if (giftWrapBlocks(this)) {
      event.preventDefault();
      return;
    }
    if (this.#timers.size === 0 && !cartMutations.busy) return;
    event.preventDefault();
    const submitter = event.submitter;
    this.#flush().then(() => {
      if (!giftWrapBlocks(this)) form.requestSubmit(submitter);
    });
  }

  #scheduleQuantity(key, input, row) {
    this.#cancelTimer(key);
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    this.#intend(key, quantity);
    const timer = setTimeout(() => {
      this.#timers.delete(key);
      this.#setLine(key, quantity, row);
    }, QUANTITY_DEBOUNCE_MS);
    this.#timers.set(key, { timer, quantity, row });
  }

  #intend(key, quantity) {
    const entry = this.#intended.get(key) ?? { quantity, writes: 0 };
    entry.quantity = quantity;
    this.#intended.set(key, entry);
    return entry;
  }

  /**
   * A morph renders the cart as of one write. While a newer quantity for
   * that line is still waiting or queued, show the newer one instead.
   */
  #restoreIntended() {
    for (const [key, { quantity, writes }] of this.#intended) {
      if (!this.#timers.has(key) && writes < 2) continue;
      const input = this.querySelector(`[data-key="${CSS.escape(key)}"] .cart__quantity-input`);
      if (input) input.value = quantity;
    }
  }

  #cancelTimer(key) {
    clearTimeout(this.#timers.get(key)?.timer);
    this.#timers.delete(key);
  }

  #flush() {
    for (const [key, { timer, quantity, row }] of this.#timers) {
      clearTimeout(timer);
      this.#timers.delete(key);
      this.#setLine(key, quantity, row);
    }
    return cartMutations.whenIdle();
  }

  async #setLine(key, quantity, row) {
    row?.setAttribute('aria-busy', 'true');
    const entry = this.#intend(key, quantity);
    entry.writes++;
    try {
      await cartMutations.change({ id: key, quantity }, 'cart-page');
    } catch (err) {
      console.error('Cart update failed:', err);
    } finally {
      entry.writes--;
      if (entry.writes === 0 && !this.#timers.has(key)) this.#intended.delete(key);
      if (row?.isConnected) row.removeAttribute('aria-busy');
    }
  }

  async #removeDiscount(code) {
    const remaining = Array.from(this.querySelectorAll('[data-discount-code]'), (el) => el.dataset.discountCode).filter(
      (c) => c !== code
    );
    try {
      await cartMutations.update({ discount: remaining.join(',') }, 'cart-page');
    } catch (err) {
      console.error('Discount removal failed:', err);
    }
  }

  #onCartUpdate(event) {
    const html = event.detail?.sections?.[this.dataset.cartSectionId];
    if (!html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-page');
    if (!next) return;
    morph(this, next, { childrenOnly: true });
    this.#restoreIntended();
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage);
}
